// src/components/Hero.js
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Play, Pause, ChevronLeft, ChevronRight } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'

// Données des images du carrousel
const heroImages = [
  {
    id: 2,
    url: "/images/auto1.webp",
    alt: "Solutions digitales avancées"
  },
  {
    id: 3,
    url: "/images/acompeniement1.webp",
    alt: "Transformation digitale"
  },
  {
    id: 4,
    url: "/images/hero/mix.webp",
    alt: "Intelligence artificielle"
  },
   {
    id: 5,
    url: "/images/about7.webp",
    alt: "Transformation digitale"
  },
   {
    id: 6,
    url: "/images/about8.webp",
    alt: "Transformation digitale"
  },
  {
    id: 6,
    url: "/images/auto2.webp",
    alt: "Transformation digitale"
  },
  {
    id: 6,
    url: "/images/secteur3.webp",
    alt: "Transformation digitale"
  },
  {
    id: 6,
    url: "/images/auto4.webp",
    alt: "Transformation digitale"
  }
]

// Composant Button avec le style du premier
const Button = ({ children, className = "", onClick, variant = "primary", size = "xl" }) => (
  <button
    onClick={onClick}
    className={`px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-full hover:shadow-2xl transition-all duration-300 shadow-lg shadow-orange-500/20 group ${className}`}
  >
    {children}
  </button>
)

// Composant pour la grille subtile (style du premier)
const AnimatedGrid = () => {
  return (
    <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>
    </div>
  )
}

// Composant pour les particules animées (style du premier)
const AnimatedParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-gradient-to-r from-orange-400/10 to-blue-500/10"
          style={{
            width: Math.random() * 60 + 20,
            height: Math.random() * 60 + 20,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, (Math.random() - 0.5) * 100],
            x: [0, (Math.random() - 0.5) * 50],
            scale: [1, 1.2, 1],
            rotate: [0, 180],
          }}
          transition={{
            duration: 8 + Math.random() * 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  )
}

const Hero = () => {
  const { language } = useLanguage()
  const t = useTranslation(language)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isAutoPlay, setIsAutoPlay] = useState(true)

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
    )
  }

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? heroImages.length - 1 : prevIndex - 1
    )
  }

  useEffect(() => {
    if (!isAutoPlay) return

    const interval = setInterval(() => {
      nextImage()
    }, 5000)

    return () => clearInterval(interval)
  }, [isAutoPlay, currentImageIndex])

  const stats = [
    { number: '150+', label: t('home.stat1') },
    { number: '50+', label: t('home.stat2') },
    { number: '15+', label: t('home.stat3') },
    { number: '98%', label: t('home.stat4') }
  ]

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-950">
      {/* Carrousel d'images de fond - Style du premier */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImageIndex}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
          >
            <img
              src={heroImages[currentImageIndex].url}
              alt={heroImages[currentImageIndex].alt}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            {/* Overlay élégant et subtil - Style du premier */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-850/75 via-slate-900/60 to-slate-950/80"></div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Grille subtile - Style du premier */}
      <AnimatedGrid />
      <AnimatedParticles />

      {/* Contrôles du carrousel - Design minimaliste du premier */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30 flex items-center gap-6">
        {/* Bouton précédent */}
        <motion.button
          onClick={prevImage}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-10 h-10 rounded-full bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 flex items-center justify-center group"
        >
          <ChevronLeft size={18} className="text-white/70 group-hover:text-white transition-colors" />
        </motion.button>

        {/* Indicateurs de slides - Style du premier */}
        <div className="flex gap-2">
          {heroImages.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              whileHover={{ scale: 1.2 }}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                index === currentImageIndex 
                  ? 'bg-orange-500 w-8' 
                  : 'bg-white/30 hover:bg-white/50 w-1.5'
              }`}
            />
          ))}
        </div>

        {/* Bouton suivant */}
        <motion.button
          onClick={nextImage}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-10 h-10 rounded-full bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 flex items-center justify-center group"
        >
          <ChevronRight size={18} className="text-white/70 group-hover:text-white transition-colors" />
        </motion.button>

        {/* Bouton play/pause */}
        <motion.button
          onClick={() => setIsAutoPlay(!isAutoPlay)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-10 h-10 rounded-full bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 flex items-center justify-center group"
        >
          {isAutoPlay ? (
            <Pause size={14} className="text-white/70 group-hover:text-white transition-colors" />
          ) : (
            <Play size={14} className="text-white/70 group-hover:text-white transition-colors ml-0.5" />
          )}
        </motion.button>
      </div>

      {/* Contenu principal - Style du premier avec contenu du deuxième */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Badge minimaliste - Style du premier */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-block px-5 py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10 mb-8"
          >
            <span className="text-white/90 font-medium text-sm tracking-wide">
              {t('home.heroBadge') || "Innovation & Excellence"}
            </span>
          </motion.div>

          {/* Titre principal - Style du premier */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="block"
            >
              {t('home.heroTitle')}
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="block mt-2"
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
                {t('home.heroTitleHighlight')}
              </span>
            </motion.span>
          </h1>

          {/* Sous-titre */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed"
          >
            {t('home.heroSubtitle')}
          </motion.p>

          {/* Bouton CTA - Style du premier */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="mb-16"
          >
            <motion.div
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <Link to="/contact">
                <Button className="group">
                  <span className="flex items-center gap-3">
                    {t('home.ctaButton')}
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Section Statistiques - Style du premier */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="text-xl font-bold text-white text-center mb-8"
            >
              {t('home.statsTitle') || "Nos Chiffres Clés"}
            </motion.h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                  whileHover={{ y: -4 }}
                  className="relative p-6 rounded-2xl border border-white/10 hover:border-orange-500/30 hover:bg-white/10 transition-all duration-300 group"
                >
                  {/* Accent subtil */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-gradient-to-r from-transparent via-orange-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className="text-3xl md:text-4xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors duration-300">
                    {stat.number}
                  </div>
                  <div className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors duration-300">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Indicateur de défilement minimaliste - Style du premier */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-6 right-8 z-20"
      >
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2"
        >
          <span className="text-white/40 text-xs uppercase tracking-wider">Scroll</span>
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center p-1">
            <motion.div
              animate={{ y: [0, 14, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-1 h-2 bg-white/40 rounded-full"
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  )
}

export default Hero